/**
 * Dev panel session state (in memory, this page only). Sandbox mode is on by
 * default once any game-affecting dev tool (show best move, auto-play, game
 * setup) has been used; the toggle overrides that either way. View-only
 * tools (wave tuning, FPS, reduced motion, hover debug) don't count.
 */
import { create } from "zustand";

import type { AutoSpeed } from "./autoPlay";
import type { GameLog } from "./sessionLog";

export interface DevSessionState {
  /** A game-affecting dev tool was used this session. */
  used: boolean;
  /** null = follow `used`. */
  sandboxOverride: boolean | null;
  showBestMove: boolean;
  speed: AutoSpeed;
  /** null = follow the OS setting. */
  forceReducedMotion: boolean | null;
  showFps: boolean;
  hoverDebug: boolean;
  /** An auto-play run is in progress (its moves are logged as "auto"). */
  autoRunning: boolean;
  games: GameLog[];
  markUsed: () => void;
  setSandbox: (on: boolean) => void;
  set: (patch: Partial<DevSessionState>) => void;
}

export const useDevSession = create<DevSessionState>((set) => ({
  used: false,
  sandboxOverride: null,
  showBestMove: false,
  speed: "realistic",
  forceReducedMotion: null,
  showFps: false,
  hoverDebug: false,
  autoRunning: false,
  games: [],
  markUsed: () => set({ used: true }),
  setSandbox: (on) => set({ sandboxOverride: on }),
  set: (patch) => set(patch),
}));

export const sandboxOn = (
  s: Pick<DevSessionState, "used" | "sandboxOverride">,
) => s.sandboxOverride ?? s.used;

/** True while finished games must not reach history, stats, achievements or telemetry. */
export const isSandboxed = (): boolean => sandboxOn(useDevSession.getState());
